import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import useEmblaCarousel from 'embla-carousel-react';
import { 
  ChevronLeft, 
  ChevronRight,
  Users,
  Target,
  Heart,
  BookOpen,
  Upload,
  Sparkles,
  MessageSquare,
  GraduationCap,
  X,
  Send,
  Loader2,
  Mail,
  User,
  Menu
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { HeroAnimated } from '@/components/HeroAnimated';
import './LandingPage.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LandingPage = () => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ 
    loop: true,
    direction: 'rtl'
  });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [howItWorksOpen, setHowItWorksOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackForm, setFeedbackForm] = useState({ name: '', email: '', message: '' });
  const [feedbackSending, setFeedbackSending] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    if (!mobileMenuOpen) return;
    const closeOnEscape = event => { if (event.key === 'Escape') setMobileMenuOpen(false); };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [mobileMenuOpen]);

  const slides = [
    {
      title: 'رشيد جي بي تي',
      highlight: 'منصتك التعليمية الذكية',
      subtitle: 'صُممت خصيصاً لمساعدة أولياء الأمور في تعليم أبنائهم خلال التحول للتعلم عن بُعد وفي الظروف الصعبة.',
      image: 'https://images.pexels.com/photos/4260325/pexels-photo-4260325.jpeg?auto=compress&cs=tinysrgb&w=1920',
    },
    {
      title: 'استمتع بالتعلم مع',
      highlight: 'معلمك الذكي',
      subtitle: 'قلل جهدك. زِد فهمك. مساعد ذكي للتعلم الأذكى.',
      image: 'https://images.pexels.com/photos/7743256/pexels-photo-7743256.jpeg?auto=compress&cs=tinysrgb&w=1920',
    },
    {
      title: 'تحويل التعليم معاً',
      highlight: 'بالحكمة والذكاء',
      subtitle: 'تمكين الطلاب والمعلمين والمؤسسات لتحقيق التميز.',
      image: 'https://images.pexels.com/photos/5212345/pexels-photo-5212345.jpeg?auto=compress&cs=tinysrgb&w=1920',
    },
    {
      title: 'أحضر الذكاء الاصطناعي',
      highlight: 'لفصلك الدراسي',
      subtitle: 'اختبر مستقبل التعليم والتعلم مع نظام التدريس الذكي.',
      image: 'https://images.pexels.com/photos/4145153/pexels-photo-4145153.jpeg?auto=compress&cs=tinysrgb&w=1920',
    }
  ];

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);
    
    const autoplay = setInterval(() => {
      if (emblaApi) emblaApi.scrollNext();
    }, 6000);
    
    return () => {
      clearInterval(autoplay);
      emblaApi.off('select', onSelect);
    };
  }, [emblaApi, onSelect]);

  return (
    <div className="landingpage h-screen overflow-hidden flex flex-col bg-[#f6f2fc] text-[#2a2140]" data-testid="landing-page">
      {/* Header - unified with other pages */}
      <header className="relative z-50 bg-[#f7f5fb] border-b border-[#e3deef]" data-testid="landing-header">
        <div className="landing-header-inner flex items-center gap-14 h-[clamp(60px,9vh,80px)] px-[clamp(20px,3vw,56px)]" dir="ltr">
          <Link to="/" className="font-extrabold text-[#1f1a33] text-[clamp(20px,2.6vh,26px)]" data-testid="logo-link">
            RasheedGPT
          </Link>
          <nav id="landing-navigation" className={`landing-navigation hidden md:flex items-center gap-[clamp(22px,2.4vw,42px)]${mobileMenuOpen ? ' is-open' : ''}`} dir="ltr" aria-label="القائمة الرئيسية" data-testid="landing-navigation" onClick={() => setMobileMenuOpen(false)}>
            <Link to="/" data-testid="landing-home-link" className="relative text-[#1f1a33] font-bold text-[clamp(14px,2vh,17px)] py-1.5 after:content-[''] after:absolute after:inset-x-0 after:-bottom-0.5 after:h-0.5 after:bg-[#4a3aa8]">الرئيسية</Link>
            <Link to="/founder" className="text-[#625c78] hover:text-[#1f1a33] transition-colors font-semibold text-[clamp(14px,2vh,17px)]" data-testid="founder-btn">المؤسس</Link>
            <Link to="/how-it-works" className="text-[#625c78] hover:text-[#1f1a33] transition-colors font-semibold text-[clamp(14px,2vh,17px)]" data-testid="how-it-works-btn">كيف يعمل</Link>
            <Link to="/subscribe" className="text-[#625c78] hover:text-[#1f1a33] transition-colors font-semibold text-[clamp(14px,2vh,17px)]" data-testid="subscribe-nav-btn">اشترك معنا</Link>
            <Link to="/feedback" className="text-[#625c78] hover:text-[#1f1a33] transition-colors font-semibold text-[clamp(14px,2vh,17px)]" data-testid="feedback-btn">رأيك يهمنا</Link>
            <Link to="/login" className="text-[#4a3aa8] hover:text-[#3b2d8c] transition-colors font-semibold text-[clamp(14px,2vh,17px)]" data-testid="header-login-btn">تسجيل الدخول</Link>
          </nav>
          <Link to="/login" className="md:hidden ms-auto text-[#4a3aa8] font-semibold text-base" data-testid="mobile-login-btn">تسجيل الدخول</Link>
          <button type="button" className="landing-menu-button" onClick={() => setMobileMenuOpen(open => !open)} aria-label={mobileMenuOpen ? 'إغلاق القائمة' : 'فتح القائمة'} aria-controls="landing-navigation" aria-expanded={mobileMenuOpen} data-testid="landing-menu-toggle">{mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}</button>
        </div>
      </header>

      {/* Hero - Animated */}
      <section className="relative flex-1 min-h-0 bg-[#f6f2fc]" data-testid="hero-section">
        <HeroAnimated />
      </section>

      {/* About Us Modal */}
      <Dialog open={aboutOpen} onOpenChange={setAboutOpen}>
        <DialogContent className="max-w-lg bg-[#1a1a2e] border border-white/10 text-white p-0 overflow-hidden max-h-[85vh]">
          <div className="relative">
            {/* Header with gradient */}
            <div className="bg-gradient-to-r from-[#4da6ff]/20 to-[#1a1a2e] p-6 pb-4">
              <DialogHeader>
                <DialogTitle className="text-2xl font-bold text-white flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[#4da6ff]/20 flex items-center justify-center">
                    <Users className="w-5 h-5 text-[#4da6ff]" />
                  </div>
                  من نحن
                </DialogTitle>
              </DialogHeader>
            </div>
            
            {/* Content */}
            <div className="p-6 pt-3 space-y-4 overflow-y-auto">
              <p className="text-white/90 text-base leading-relaxed">
                <span className="text-[#4da6ff] font-bold">RasheedGPT</span> هي منصة تعليمية ذكية صُممت خصيصاً لمساعدة أولياء الأمور في تعليم أبنائهم خلال التحول للتعلم عن بُعد وفي الظروف الصعبة.
              </p>
              
              <div className="grid gap-4">
                <div className="flex items-start gap-4 p-4 rounded-xl bg-white/5 border border-white/10">
                  <div className="w-10 h-10 rounded-lg bg-[#4da6ff]/20 flex items-center justify-center flex-shrink-0">
                    <Target className="w-5 h-5 text-[#4da6ff]" />
                  </div>
                  <div>
                    <h4 className="font-bold text-white mb-1">هدفنا</h4>
                    <p className="text-white/70 text-sm leading-relaxed">
                      تمكين أولياء الأمور من أن يكونوا معلمين فعّالين لأبنائهم، وتوفير أدوات تعليمية ذكية تجعل التعلم المنزلي أسهل وأكثر متعة.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-3 rounded-xl bg-white/5 border border-white/10">
                  <div className="w-8 h-8 rounded-lg bg-[#4da6ff]/20 flex items-center justify-center flex-shrink-0">
                    <Heart className="w-4 h-4 text-[#4da6ff]" />
                  </div>
                  <div>
                    <h4 className="font-bold text-white text-sm mb-1">رؤيتنا</h4>
                    <p className="text-white/70 text-xs leading-relaxed">
                      نؤمن بأن كل طفل يستحق تعليماً عالي الجودة، وأن التكنولوجيا يمكن أن تكون جسراً يربط بين المنزل والمدرسة في أصعب الظروف.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-3 rounded-xl bg-white/5 border border-white/10">
                  <div className="w-8 h-8 rounded-lg bg-[#4da6ff]/20 flex items-center justify-center flex-shrink-0">
                    <GraduationCap className="w-4 h-4 text-[#4da6ff]" />
                  </div>
                  <div>
                    <h4 className="font-bold text-white text-sm mb-1">رسالتنا</h4>
                    <p className="text-white/70 text-xs leading-relaxed">
                      نقدم محتوى تعليمي مُصمم بعناية مع أدوات الذكاء الاصطناعي لمساعدة الأسر على مواصلة رحلة التعلم مهما كانت التحديات.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* How It Works Modal */}
      <Dialog open={howItWorksOpen} onOpenChange={setHowItWorksOpen}>
        <DialogContent className="max-w-lg bg-[#1a1a2e] border border-white/10 text-white p-0 overflow-hidden max-h-[85vh]">
          <div className="relative">
            {/* Header with gradient */}
            <div className="bg-gradient-to-r from-[#4da6ff]/20 to-[#1a1a2e] p-6 pb-4">
              <DialogHeader>
                <DialogTitle className="text-2xl font-bold text-white flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[#4da6ff]/20 flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-[#4da6ff]" />
                  </div>
                  كيف يعمل
                </DialogTitle>
              </DialogHeader>
            </div>
            
            {/* Content */}
            <div className="p-6 pt-3 space-y-3 overflow-y-auto">
              <p className="text-white/80 text-sm leading-relaxed mb-4">
                استخدام RasheedGPT سهل وبسيط. اتبع هذه الخطوات للبدء:
              </p>
              
              {/* Steps */}
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#4da6ff] flex items-center justify-center flex-shrink-0 text-white font-bold text-sm">
                    1
                  </div>
                  <div className="flex-1 p-3 rounded-xl bg-white/5 border border-white/10">
                    <div className="flex items-center gap-2 mb-1">
                      <BookOpen className="w-4 h-4 text-[#4da6ff]" />
                      <h4 className="font-bold text-white text-sm">اختر الصف والمادة</h4>
                    </div>
                    <p className="text-white/70 text-xs">
                      سجّل الدخول واختر الصف الدراسي لابنك، ثم اختر المادة والدرس المطلوب.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#4da6ff] flex items-center justify-center flex-shrink-0 text-white font-bold text-sm">
                    2
                  </div>
                  <div className="flex-1 p-3 rounded-xl bg-white/5 border border-white/10">
                    <div className="flex items-center gap-2 mb-1">
                      <Sparkles className="w-4 h-4 text-[#4da6ff]" />
                      <h4 className="font-bold text-white text-sm">استخدم أدوات الذكاء الاصطناعي</h4>
                    </div>
                    <p className="text-white/70 text-xs">
                      احصل على نصائح التعلم، أوراق العمل، الاختبارات القصيرة، وقائمة المصطلحات المهمة - كلها مُولّدة خصيصاً للدرس.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#4da6ff] flex items-center justify-center flex-shrink-0 text-white font-bold text-sm">
                    3
                  </div>
                  <div className="flex-1 p-3 rounded-xl bg-white/5 border border-white/10">
                    <div className="flex items-center gap-2 mb-1">
                      <Upload className="w-4 h-4 text-[#4da6ff]" />
                      <h4 className="font-bold text-white text-sm">حل الأسئلة بالصورة</h4>
                    </div>
                    <p className="text-white/70 text-xs">
                      صوّر أي سؤال يواجه ابنك وارفعه للمنصة، وسيقدم لك الذكاء الاصطناعي الحل خطوة بخطوة.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#4da6ff] flex items-center justify-center flex-shrink-0 text-white font-bold text-sm">
                    4
                  </div>
                  <div className="flex-1 p-3 rounded-xl bg-white/5 border border-white/10">
                    <div className="flex items-center gap-2 mb-1">
                      <MessageSquare className="w-4 h-4 text-[#4da6ff]" />
                      <h4 className="font-bold text-white text-sm">تعلّم وعلّم معاً</h4>
                    </div>
                    <p className="text-white/70 text-xs">
                      استخدم المحتوى المُولّد لشرح الدروس لابنك بطريقة مبسطة وممتعة، وتابع تقدمه التعليمي.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Feedback Modal */}
      <Dialog open={feedbackOpen} onOpenChange={setFeedbackOpen}>
        <DialogContent className="max-w-lg bg-[#1a1a2e] border border-white/10 text-white p-0 overflow-hidden max-h-[80vh]">
          <div className="relative overflow-y-auto max-h-[80vh]">
            {/* Header with gradient */}
            <div className="bg-gradient-to-r from-[#4da6ff]/20 to-[#1a1a2e] p-5 pb-3">
              <DialogHeader>
                <DialogTitle className="text-2xl font-bold text-white flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[#4da6ff]/20 flex items-center justify-center">
                    <Mail className="w-5 h-5 text-[#4da6ff]" />
                  </div>
                  <div>
                    رأيك يهمنا
                    <p className="text-xs text-white/50 font-normal mt-1">ساعدنا نطوّر المنصة معاً</p>
                  </div>
                </DialogTitle>
              </DialogHeader>
            </div>
            
            <div className="p-5 pt-2 space-y-3">
              <p className="text-white/70 text-sm leading-relaxed p-3 rounded-xl bg-white/5 border border-white/10">
                نحب نسمع منك! سواء كانت ملاحظة، اقتراح، أو حتى كلمة تشجيع - كل رأي يساعدنا نصنع منصة أفضل لأبنائنا.
              </p>
              
              <div className="space-y-3">
                <div>
                  <label className="text-white/70 text-xs font-medium block mb-1.5 flex items-center gap-1.5">
                    <User className="w-3.5 h-3.5 text-[#4da6ff]" />
                    الاسم
                  </label>
                  <input
                    type="text"
                    value={feedbackForm.name}
                    onChange={(e) => setFeedbackForm(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="أدخل اسمك"
                    dir="rtl"
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-[#4da6ff]/50 focus:ring-1 focus:ring-[#4da6ff]/30 text-sm transition-all duration-200"
                    data-testid="feedback-name-input"
                  />
                </div>
                
                <div>
                  <label className="text-white/70 text-xs font-medium block mb-1.5 flex items-center gap-1.5">
                    <Mail className="w-3.5 h-3.5 text-[#4da6ff]" />
                    البريد الإلكتروني
                  </label>
                  <input
                    type="email"
                    value={feedbackForm.email}
                    onChange={(e) => setFeedbackForm(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="example@email.com"
                    dir="ltr"
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-[#4da6ff]/50 focus:ring-1 focus:ring-[#4da6ff]/30 text-sm text-left transition-all duration-200"
                    data-testid="feedback-email-input"
                  />
                </div>
                
                <div>
                  <label className="text-white/70 text-xs font-medium block mb-1.5 flex items-center gap-1.5">
                    <Send className="w-3.5 h-3.5 text-[#4da6ff]" />
                    رسالتك
                  </label>
                  <textarea
                    value={feedbackForm.message}
                    onChange={(e) => setFeedbackForm(prev => ({ ...prev, message: e.target.value }))}
                    placeholder="شاركنا ملاحظاتك أو اقتراحاتك..."
                    dir="rtl"
                    rows={3}
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-[#4da6ff]/50 focus:ring-1 focus:ring-[#4da6ff]/30 text-sm resize-none transition-all duration-200"
                    data-testid="feedback-message-input"
                  />
                </div>
              </div>
              
              <Button
                onClick={async () => {
                  if (!feedbackForm.name || !feedbackForm.email || !feedbackForm.message) {
                    toast.error('يرجى تعبئة جميع الحقول');
                    return;
                  }
                  setFeedbackSending(true);
                  try {
                    await axios.post(`${API_URL}/api/feedback`, feedbackForm);
                    toast.success('تم إرسال رسالتك بنجاح! شكراً لك');
                    setFeedbackForm({ name: '', email: '', message: '' });
                    setFeedbackOpen(false);
                  } catch (error) {
                    toast.error('فشل في إرسال الرسالة، حاول مرة أخرى');
                  } finally {
                    setFeedbackSending(false);
                  }
                }}
                disabled={feedbackSending || !feedbackForm.name || !feedbackForm.email || !feedbackForm.message}
                className="w-full bg-[#4da6ff] hover:bg-[#7bbfff] text-white rounded-xl h-12 font-bold disabled:opacity-30 transition-all duration-300"
                data-testid="feedback-submit-btn"
              >
                {feedbackSending ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Send className="w-4 h-4 me-2" />
                    إرسال الرسالة
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LandingPage;
